import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';

import {
  MdOutlineArrowBackIos,
  MdOutlineArrowForwardIos,
} from 'react-icons/md';

import CourseCardV3 from '../../Atoms/CourseCardV3';
import ShimmerCourseCard from '../../Atoms/Shimmer/CourseCard';

import { Course as CourseInterface } from '../../../models/CourseModels';

import { Viewport, Track, Item, Arrow } from './styles';

interface CourseContainerProps {
  courses: Array<CourseInterface>;
  isLoading: boolean;
  courseViewType?: string;
  /** Nomeia a fileira nas setas ("Rolar Em alta para a direita"). */
  label?: string;
}

const SKELETON_COUNT = 8;

interface EdgeState {
  overflows: boolean;
  atStart: boolean;
  atEnd: boolean;
}

const CourseContainer: React.FC<CourseContainerProps> = ({
  courses,
  isLoading,
  courseViewType = 'horizontal',
  label = '',
}) => {
  const trackRef = useRef<HTMLUListElement>(null);
  const frame = useRef<number | null>(null);
  const [edge, setEdge] = useState<EdgeState>({
    overflows: false,
    atStart: true,
    atEnd: false,
  });

  const measure = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;

    const max = el.scrollWidth - el.clientWidth;

    setEdge({
      overflows: max > 1,
      atStart: el.scrollLeft <= 1,
      atEnd: el.scrollLeft >= max - 1,
    });
  }, []);

  /**
   * Passo de rolagem: quantos cards INTEIROS cabem na vista, medidos do DOM.
   * A versão anterior usava `window.innerWidth / 2` e uma largura de card
   * chumbada em 280 — dois números que nunca corresponderam ao que estava na
   * tela. Aqui nada é presumido.
   */
  const step = useCallback(() => {
    const el = trackRef.current;
    if (!el) return 0;

    const first = el.firstElementChild as HTMLElement | null;
    if (!first) return el.clientWidth;

    const styles = window.getComputedStyle(first);
    const unit =
      first.getBoundingClientRect().width +
      parseFloat(styles.marginRight || '0');

    if (!unit) return el.clientWidth;

    // Desconta a goteira de 5% de cada lado.
    const usable = el.clientWidth * 0.9;

    return Math.max(1, Math.floor(usable / unit)) * unit;
  }, []);

  const scrollByPage = useCallback(
    (direction: 1 | -1) => {
      const el = trackRef.current;
      if (!el) return;

      const delta = step() * direction;

      if (typeof el.scrollBy === 'function') {
        try {
          el.scrollBy({ left: delta, behavior: 'smooth' });
          return;
        } catch {
          /* Safari antigo não aceita o objeto de opções. */
        }
      }

      el.scrollLeft += delta;
    },
    [step],
  );

  const handleScroll = useCallback(() => {
    if (frame.current !== null) return;

    frame.current = window.requestAnimationFrame(() => {
      frame.current = null;
      measure();
    });
  }, [measure]);

  /**
   * A medição anterior rodava num useLayoutEffect que dependia só de
   * `isLoading`: nunca remedia em resize, então as setas sumiam ou sobravam
   * na hora errada.
   */
  useLayoutEffect(() => {
    measure();

    const el = trackRef.current;
    if (!el) return undefined;

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(measure);
      observer.observe(el);
      return () => observer.disconnect();
    }

    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure, courses.length, isLoading]);

  if (isLoading) {
    return (
      <Viewport>
        <Track aria-hidden="true">
          {Array.from({ length: SKELETON_COUNT }, (_, index) => (
            <Item key={index}>
              <ShimmerCourseCard
                fluid
                courseViewType={courseViewType}
                animationDelay={0.12 * index}
              />
            </Item>
          ))}
        </Track>
      </Viewport>
    );
  }

  const showArrows = edge.overflows;

  return (
    <Viewport>
      {showArrows && (
        <Arrow
          type="button"
          $side="left"
          onClick={() => scrollByPage(-1)}
          disabled={edge.atStart}
          aria-label={`Rolar ${label} para a esquerda`}
        >
          <MdOutlineArrowBackIos aria-hidden="true" focusable="false" />
        </Arrow>
      )}

      <Track ref={trackRef} onScroll={handleScroll}>
        {courses.map(
          (course) =>
            course && (
              <Item key={course.courseid}>
                <CourseCardV3 course={course} />
              </Item>
            ),
        )}
      </Track>

      {showArrows && (
        <Arrow
          type="button"
          $side="right"
          onClick={() => scrollByPage(1)}
          disabled={edge.atEnd}
          aria-label={`Rolar ${label} para a direita`}
        >
          <MdOutlineArrowForwardIos aria-hidden="true" focusable="false" />
        </Arrow>
      )}
    </Viewport>
  );
};

export default CourseContainer;
